import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.jsx';
import { Button } from '../components/ui/button.jsx';
import { Input } from '../components/ui/input.jsx';
import { Textarea } from '../components/ui/textarea.jsx';
import { Label } from '../components/ui/label.jsx';
import { Badge } from '../components/ui/badge.jsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select.jsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog.jsx';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table.jsx';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Settings,
  Server,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar
} from 'lucide-react';
import { supabase } from '../api/supabaseClient.js';
import { User, System } from '../api/supabaseEntities.js';
import { format } from 'date-fns';

// Função utilitária para criar URLs de páginas
const createPageUrl = (pageName, params = '') => {
  const basePath = '/' + pageName.toLowerCase().replace(/ /g, '-');
  return params ? `${basePath}/${params}` : basePath;
};

export default function Systems() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [systems, setSystems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Estados do modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingSystem, setEditingSystem] = useState(null);
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    version: '',
    company_id: '',
    status: 'active',
    type: '',
    license_type: '',
    installation_date: '',
    expiration_date: '',
    support_level: '',
    documentation_url: '',
    repository_url: '',
    system_code: '',
    system_category: '',
    system_subcategory: '',
    system_notes: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      console.log('🚀 Iniciando loadInitialData...');
      setIsLoading(true);
      
      // Verificar se é usuário interno
      console.log('👤 Verificando usuário...');
      const userData = await User.me();
      console.log('✅ Usuário carregado:', userData);
      setCurrentUser(userData);
      
      if (userData.role === 'client') {
        console.log('🚫 Usuário é cliente, redirecionando...');
        navigate(createPageUrl('Tickets'));
        return;
      }
      
      console.log('📊 Carregando dados com userData...');
      await loadSystems(userData);
      await loadCompanies();
      
      console.log('✅ Dados carregados com sucesso!');
    } catch (error) {
      console.error('💥 Erro ao carregar dados:', error);
    } finally {
      setIsLoading(false);
      console.log('🏁 loadInitialData finalizado');
    }
  };

  const loadSystems = async (userData = null) => {
    try {
      const userToUse = userData || currentUser;
      console.log('🔄 Carregando sistemas via entidade System...');
      console.log('👤 User para carregamento:', userToUse);
      
      // Usar a entidade System que já tem todas as estratégias de fallback implementadas
      const systemsArray = await System.list();
      
      console.log(`✅ ${systemsArray.length} sistemas carregados via entidade System:`, systemsArray);
      setSystems(systemsArray);
      
    } catch (error) {
      console.error('💥 Erro ao carregar sistemas:', error);
      // Em caso de erro, definir array vazio para não quebrar a aplicação
      setSystems([]);
    }
  };

  const loadCompanies = async () => {
    try {
      console.log('🔄 Carregando empresas via query direta...');
      
      // Query direta para evitar problemas de ambiguidade
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, company_name, status, created_at, updated_at')
        .eq('status', 'active')
        .order('name');
      
      console.log('📊 Resultado query companies:', { data, error });
      
      if (error) {
        console.error('❌ Erro na query:', error);
        throw error;
      }
      
      const companiesArray = data || [];
      console.log(`✅ ${companiesArray.length} empresas carregadas:`, companiesArray);
      setCompanies(companiesArray);
      
    } catch (error) {
      console.error('💥 Erro ao carregar empresas:', error);
      // Em caso de erro, definir array vazio para não quebrar a aplicação
      setCompanies([]);
    }
  };

  const handleOpenModal = (system = null) => {
    if (system) {
      setIsEditing(true);
      setEditingSystem(system);
      setFormData({
        name: system.name || '',
        description: system.description || '',
        version: system.version || '',
        company_id: system.company_id || '',
        status: system.status || 'active',
        type: system.type || '',
        license_type: system.license_type || '',
        installation_date: system.installation_date || '',
        expiration_date: system.expiration_date || '',
        support_level: system.support_level || '',
        documentation_url: system.documentation_url || '',
        repository_url: system.repository_url || '',
        system_code: system.system_code || '',
        system_category: system.system_category || '',
        system_subcategory: system.system_subcategory || '',
        system_notes: system.system_notes || ''
      });
    } else {
      setIsEditing(false);
      setEditingSystem(null);
      setFormData({
        name: '',
        description: '',
        version: '',
        company_id: '',
        status: 'active',
        type: '',
        license_type: '',
        installation_date: '',
        expiration_date: '',
        support_level: '',
        documentation_url: '',
        repository_url: '',
        system_code: '',
        system_category: '',
        system_subcategory: '',
        system_notes: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setEditingSystem(null);
    setFormData({
      name: '',
      description: '',
      version: '',
      company_id: '',
      status: 'active',
      type: '',
      license_type: '',
      installation_date: '',
      expiration_date: '',
      support_level: '',
      documentation_url: '',
      repository_url: '',
      system_code: '',
      system_category: '',
      system_subcategory: '',
      system_notes: ''
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      
      console.log('🔄 Salvando sistema...');
      
      const systemData = {
        ...formData,
        company_id: formData.company_id || null,
        installation_date: formData.installation_date || null,
        expiration_date: formData.expiration_date || null
      };
      
      // Obter o ID do usuário autenticado usando a mesma estratégia do resto da aplicação
      const authUser = await User.getCurrentUser();
      if (!authUser) {
        throw new Error('Usuário não autenticado');
      }
      
      const authUserId = authUser.id; // ID do auth.users
      
      if (isEditing) {
        // Atualizar sistema existente
        systemData.updated_at = new Date().toISOString();
        
        const result = await System.update(editingSystem.id, systemData);
        console.log('✅ Sistema atualizado com sucesso:', result);
      } else {
        // Criar novo sistema
        systemData.created_by = authUserId;
        systemData.created_at = new Date().toISOString();
        systemData.updated_at = new Date().toISOString();
        
        const result = await System.create(systemData);
        console.log('✅ Sistema criado com sucesso:', result);
      }
      
      // Recarregar lista de sistemas
      await loadSystems();
      
      // Fechar modal
      handleCloseModal();
      
    } catch (error) {
      console.error('Erro ao salvar sistema:', error);
      alert('Erro ao salvar sistema: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (system) => {
    if (!confirm(`Tem certeza que deseja excluir o sistema "${system.name}"?`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('systems')
        .delete()
        .eq('id', system.id);
      
      if (error) throw error;
      
      await loadSystems();
    } catch (error) {
      console.error('Erro ao excluir sistema:', error);
      alert('Erro ao excluir sistema: ' + error.message);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const filteredSystems = systems.filter(system => {
    const matchesSearch = system.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         system.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         system.version?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || system.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  // Debug: log quando systems muda
  useEffect(() => {
    console.log('🔄 Estado systems atualizado:', systems);
    console.log('📊 Total de sistemas:', systems.length);
    console.log('🔍 Sistemas filtrados:', filteredSystems.length);
  }, [systems, filteredSystems]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
                <div>
          <h1 className="text-3xl font-bold">Sistemas</h1>
          <p className="text-muted-foreground">
            Gerencie os sistemas de software da empresa
          </p>
                </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Sistema
        </Button>
              </div>

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filtros
          </CardTitle>
          </CardHeader>
          <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Nome, descrição ou versão..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="active">Ativo</SelectItem>
                  <SelectItem value="inactive">Inativo</SelectItem>
                  <SelectItem value="maintenance">Manutenção</SelectItem>
                  <SelectItem value="deprecated">Descontinuado</SelectItem>
                </SelectContent>
              </Select>
              </div>

            <div className="flex items-end">
              <Button
                variant="outline" 
                onClick={() => {
                  setSearchTerm('');
                  setFilterStatus('all');
                }}
              >
                Limpar Filtros
              </Button>
            </div>
            </div>
          </CardContent>
        </Card>

      {/* Tabela de Sistemas */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Server className="h-5 w-5" />
            Sistemas Cadastrados
            <Badge variant="secondary" className="ml-auto">
              {filteredSystems.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Sistema</TableHead>
                <TableHead>Empresa</TableHead>
                <TableHead>Versão</TableHead>
                <TableHead>Instalação</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSystems.map((system) => (
                <TableRow key={system.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{system.name}</div>
                      {system.description && (
                        <div className="text-sm text-muted-foreground">{system.description}</div>
                      )}
                      {system.system_code && (
                        <div className="text-xs text-muted-foreground">Código: {system.system_code}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                                            {system.company_name || 'Geral'}
                  </TableCell>
                  <TableCell>
                    {system.version && (
                      <Badge variant="outline">v{system.version}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {system.installation_date && (
                      <div className="flex items-center gap-1 text-sm">
                        <Calendar className="h-3 w-3" />
                        {formatDate(system.installation_date)}
                  </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={
                        system.status === 'active' ? 'success' : 
                        system.status === 'inactive' ? 'secondary' : 
                        system.status === 'maintenance' ? 'warning' : 'destructive'
                      }
                    >
                      {system.status === 'active' && <CheckCircle className="h-3 w-3 mr-1" />}
                      {system.status === 'inactive' && <XCircle className="h-3 w-3 mr-1" />}
                      {system.status === 'maintenance' && <Settings className="h-3 w-3 mr-1" />}
                      {system.status === 'deprecated' && <AlertTriangle className="h-3 w-3 mr-1" />}
                      {system.status === 'active' ? 'Ativo' : 
                       system.status === 'inactive' ? 'Inativo' : 
                       system.status === 'maintenance' ? 'Manutenção' : 'Descontinuado'}
                    </Badge>
                  </TableCell>
                  <TableCell>
        <div className="flex gap-2">
          <Button 
                        size="sm"
                        variant="outline"
                        onClick={() => handleOpenModal(system)}
          >
                        <Edit className="h-4 w-4" />
          </Button>
          <Button 
                        size="sm"
            variant="outline"
                        onClick={() => handleDelete(system)}
                        className="text-red-600 hover:text-red-700"
          >
                        <Trash2 className="h-4 w-4" />
          </Button>
        </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredSystems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    Nenhum sistema encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal de Cadastro/Edição */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEditing ? 'Editar Sistema' : 'Novo Sistema'}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? 'Altere as informações do sistema abaixo.' : 'Preencha as informações para cadastrar um novo sistema.'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome do Sistema *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Ex: Siacom, ERP Manager"
                  required
                />
      </div>

              <div className="space-y-2">
                <Label htmlFor="version">Versão</Label>
              <Input
                  id="version"
                  value={formData.version}
                  onChange={(e) => handleInputChange('version', e.target.value)}
                  placeholder="Ex: 1.0.0, 2.5.3"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Descreva as funcionalidades principais do sistema"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="company_id">Empresa</Label>
                <Select 
                  value={formData.company_id} 
                  onValueChange={(value) => handleInputChange('company_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sistema geral (todas as empresas)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Sistema geral (todas as empresas)</SelectItem>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name || company.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
            <Select
                  value={formData.status} 
                  onValueChange={(value) => handleInputChange('status', value)}
            >
                  <SelectTrigger>
                    <SelectValue />
              </SelectTrigger>
              <SelectContent>
                    <SelectItem value="active">Ativo</SelectItem>
                    <SelectItem value="inactive">Inativo</SelectItem>
                    <SelectItem value="maintenance">Em Manutenção</SelectItem>
                    <SelectItem value="deprecated">Descontinuado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="system_code">Código do Sistema</Label>
                <Input
                  id="system_code"
                  value={formData.system_code}
                  onChange={(e) => handleInputChange('system_code', e.target.value)}
                  placeholder="Ex: SIA, ERP, CRM"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type">Tipo</Label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="erp">ERP</SelectItem>
                    <SelectItem value="crm">CRM</SelectItem>
                    <SelectItem value="fiscal">Fiscal</SelectItem>
                    <SelectItem value="financeiro">Financeiro</SelectItem>
                    <SelectItem value="pdv">PDV</SelectItem>
                    <SelectItem value="outros">Outros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="system_category">Categoria</Label>
                <Input
                  id="system_category"
                  value={formData.system_category}
                  onChange={(e) => handleInputChange('system_category', e.target.value)}
                  placeholder="Ex: Gestão, Vendas, Fiscal"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="system_subcategory">Subcategoria</Label>
                <Input
                  id="system_subcategory"
                  value={formData.system_subcategory}
                  onChange={(e) => handleInputChange('system_subcategory', e.target.value)}
                  placeholder="Ex: Estoque, Faturamento"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="installation_date">Data de Instalação</Label>
                <Input
                  id="installation_date"
                  type="date"
                  value={formData.installation_date}
                  onChange={(e) => handleInputChange('installation_date', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expiration_date">Data de Expiração</Label>
                <Input
                  id="expiration_date"
                  type="date"
                  value={formData.expiration_date}
                  onChange={(e) => handleInputChange('expiration_date', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="license_type">Tipo de Licença</Label>
                <Select value={formData.license_type} onValueChange={(value) => handleInputChange('license_type', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de licença" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="perpetua">Perpétua</SelectItem>
                    <SelectItem value="assinatura">Assinatura</SelectItem>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="opensource">Open Source</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="support_level">Nível de Suporte</Label>
                <Select value={formData.support_level} onValueChange={(value) => handleInputChange('support_level', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o nível de suporte" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basico">Básico</SelectItem>
                    <SelectItem value="intermediario">Intermediário</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="empresarial">Empresarial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="documentation_url">URL da Documentação</Label>
                <Input
                  id="documentation_url"
                  value={formData.documentation_url}
                  onChange={(e) => handleInputChange('documentation_url', e.target.value)}
                  placeholder="https://docs.sistema.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="repository_url">URL do Repositório</Label>
                <Input
                  id="repository_url"
                  value={formData.repository_url}
                  onChange={(e) => handleInputChange('repository_url', e.target.value)}
                  placeholder="https://github.com/empresa/sistema"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="system_notes">Observações</Label>
              <Textarea
                id="system_notes"
                value={formData.system_notes}
                onChange={(e) => handleInputChange('system_notes', e.target.value)}
                placeholder="Observações gerais sobre o sistema..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={isSaving || !formData.name}
            >
              {isSaving ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Criar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}